import { motion } from 'framer-motion';
import { SectionHeading } from './Features';
import { LINKS } from '../config';

const CHECKLIST = [
  'AES-GCM vault encryption, keys derived with PBKDF2',
  'No decrypted seed ever persisted to browser storage',
  'Mnemonic-only recovery — nothing to lose but your phrase',
  'Locked on restart, and again after idle timeout',
  'Every dApp connection requires explicit, revocable approval',
];

const LOG_LINES = [
  { t: '01', label: 'derive_key()', detail: 'PBKDF2 · password + salt' },
  { t: '02', label: 'seal_vault()', detail: 'AES-GCM · Web Crypto' },
  { t: '03', label: 'store()', detail: 'IndexedDB · encrypted only' },
  { t: '04', label: 'lock()', detail: 'idle timeout reached' },
];

export function Security() {
  return (
    <section id="security" className="relative border-t border-white/[0.05] bg-midnight-900/40 py-28">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-16 px-6 lg:grid-cols-2 lg:items-center">
        <div>
          <SectionHeading
            eyebrow="Security model"
            title="Nothing leaves your device unencrypted."
            body="Vela is designed so that a compromised website, a synced browser profile, or a nosy extension can't get anywhere near your keys."
          />

          <ul className="mt-9 space-y-4">
            {CHECKLIST.map((item, i) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="flex items-start gap-3 text-sm text-mist"
              >
                <CheckGlyph />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-9 rounded-xl border border-teal/20 bg-teal/[0.05] px-5 py-4 text-sm text-mist"
          >
            <span className="font-medium text-teal-soft">Honest status:</span> Vela is
            audit-pending software. It behaves like a secure wallet in daily use, but
            mainnet distribution is gated on an external security audit before it's
            called finished.{' '}
            <a href={LINKS.securityPolicy} className="underline decoration-teal/40 underline-offset-4 hover:text-star">
              Read the security policy
            </a>
            .
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl border border-white/[0.07] bg-midnight-950 p-6 font-mono text-sm shadow-card"
        >
          <div className="mb-5 flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
            <span className="ml-3 text-xs text-mist/60">vault.log</span>
          </div>
          <div className="space-y-3.5">
            {LOG_LINES.map((line, i) => (
              <motion.div
                key={line.t}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.18 }}
                className="flex items-baseline gap-3"
              >
                <span className="text-mist/40">{line.t}</span>
                <span className="text-teal-soft">{line.label}</span>
                <span className="text-mist/70">{line.detail}</span>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 1.1 }}
              className="flex items-center gap-2 pt-1 text-star"
            >
              <span className="h-1.5 w-1.5 animate-twinkle rounded-full bg-teal" />
              vault sealed
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function CheckGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      className="mt-0.5 flex-none text-teal-soft"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.3" opacity="0.35" />
      <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
